import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { ShieldCheck, Save, Check, RotateCcw, Copy, History } from "lucide-react";
import { ukDateTime } from "../lib/dates";

const CARD = "bg-white rounded-2xl border border-[#EBE9E4] shadow-[0_8px_30px_rgba(45,45,45,0.04)]";

export default function RolesPermissions() {
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [matrix, setMatrix] = useState({});
  const [teams, setTeams] = useState([]);
  const [scopeTeam, setScopeTeam] = useState("");
  const [hasOverride, setHasOverride] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [copyFrom, setCopyFrom] = useState("");
  const [log, setLog] = useState([]);
  const [showLog, setShowLog] = useState(false);

  const loadLog = async () => {
    try { const { data } = await api.get("/permission-audit"); setLog(data.entries); } catch { /* noop */ }
  };
  const load = async (teamId = scopeTeam) => {
    try {
      const { data } = await api.get("/role-permissions", { params: teamId ? { team_id: teamId } : {} });
      setRoles(data.roles); setPermissions(data.permissions); setCatalog(data.catalog || []); setMatrix(data.matrix);
      setTeams(data.teams || []);
      setHasOverride(!!data.has_override);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(""); loadLog(); }, []);

  const onScopeChange = (v) => { setScopeTeam(v); setCopyFrom(""); setLoading(true); load(v); };

  const isLocked = (roleKey) => (roles.find((r) => r.key === roleKey) || {}).locked;
  const toggle = (roleKey, permKey) => {
    if (isLocked(roleKey)) return;
    setMatrix((m) => ({ ...m, [roleKey]: { ...m[roleKey], [permKey]: !m[roleKey]?.[permKey] } }));
  };
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/role-permissions", scopeTeam ? { matrix, team_id: scopeTeam } : { matrix });
      toast.success(scopeTeam ? "Team permissions saved" : "Global role permissions saved");
      load(); loadLog();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const resetTeam = async () => {
    setSaving(true);
    try {
      await api.put("/role-permissions", { team_id: scopeTeam, reset: true, matrix: {} });
      toast.success("Team reset to the global default");
      load(); loadLog();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const doCopy = async () => {
    if (!scopeTeam) return;
    setSaving(true);
    try {
      const { data } = await api.post("/role-permissions/copy", { from_team_id: copyFrom, to_team_id: scopeTeam });
      toast.success(`Copied permissions from ${data.from}`);
      setCopyFrom(""); load(); loadLog();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (loading) return <div className={`${CARD} p-8 text-center max-w-6xl mx-auto`} style={{ color: "var(--sj-text-3)" }}>Loading roles…</div>;

  return (
    <div className="max-w-6xl mx-auto space-y-6" data-testid="roles-permissions-page">
      <div className="sj-page-hero flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-head text-3xl font-extrabold tracking-tight flex items-center gap-2"><ShieldCheck className="h-7 w-7" style={{ color: "var(--sj-primary)" }} /> Roles &amp; Permissions</h1>
          <p style={{ color: "var(--sj-text-2)" }}>Tick what each role is allowed to do. Administrators always have full access.</p>
        </div>
        <div className="flex items-center gap-2">
          {scopeTeam && hasOverride && (
            <button data-testid="reset-team-permissions-btn" onClick={resetTeam} disabled={saving} className="inline-flex items-center gap-2 rounded-full px-4 py-2.5 font-semibold text-sm border disabled:opacity-60" style={{ borderColor: "var(--sj-border)", color: "var(--sj-secondary)" }}>
              <RotateCcw className="h-4 w-4" /> Reset to global
            </button>
          )}
          <button data-testid="save-role-permissions-btn" onClick={save} disabled={saving} className="inline-flex items-center gap-2 rounded-full px-6 py-2.5 text-white font-semibold text-sm disabled:opacity-60" style={{ background: "var(--sj-primary)" }}>
            <Save className="h-4 w-4" /> {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>

      <div className={`${CARD} p-4 flex flex-wrap items-center gap-3`}>
        <label className="text-sm font-medium" style={{ color: "var(--sj-text-2)" }}>Applies to</label>
        <select data-testid="roles-scope-select" value={scopeTeam} onChange={(e) => onScopeChange(e.target.value)}
          className="bg-[#EDF2F4] border border-[#EBE9E4] rounded-xl px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#0B5E4E]/20">
          <option value="">Global default (all teams)</option>
          {teams.map((t) => <option key={t.id} value={t.id}>{t.name}{t.has_override ? " ✎" : ""}</option>)}
        </select>
        <span className="text-sm" style={{ color: "var(--sj-text-3)" }}>
          {scopeTeam
            ? (hasOverride ? "This team has its own permissions (overrides the global default)." : "Showing the global default — save to give this team its own permissions.")
            : "Editing the global default used by every team unless a team overrides it."}
        </span>
        {scopeTeam && (
          <div className="w-full flex flex-wrap items-center gap-2 pt-2 mt-1 border-t" style={{ borderColor: "var(--sj-border)" }}>
            <Copy className="h-4 w-4" style={{ color: "var(--sj-text-3)" }} />
            <span className="text-sm font-medium" style={{ color: "var(--sj-text-2)" }}>Copy grid from</span>
            <select data-testid="copy-from-select" value={copyFrom} onChange={(e) => setCopyFrom(e.target.value)}
              className="bg-[#EDF2F4] border border-[#EBE9E4] rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#0B5E4E]/20">
              <option value="">Global default</option>
              {teams.filter((t) => t.id !== scopeTeam).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            <button data-testid="copy-permissions-btn" onClick={doCopy} disabled={saving}
              className="inline-flex items-center gap-1.5 text-sm font-semibold rounded-full px-4 py-2 border hover:bg-[#EDF2F4] disabled:opacity-60" style={{ borderColor: "var(--sj-border)", color: "var(--sj-secondary)" }}>
              Copy to this team
            </button>
          </div>
        )}
      </div>

      <div className={`${CARD} p-2 sm:p-4 overflow-x-auto`}>
        <table className="min-w-full text-sm border-collapse">
          <thead>
            <tr>
              <th className="text-left py-3 px-3 sticky left-0 bg-white" style={{ color: "var(--sj-text-3)" }}>Menu item</th>
              {roles.map((r) => (
                <th key={r.key} className="py-3 px-3 text-center align-bottom" data-testid={`role-col-${r.key}`}>
                  <div className="font-semibold" style={{ color: "var(--sj-text)" }}>{r.label}</div>
                  {r.locked && <div className="text-[10px] uppercase tracking-wide mt-0.5" style={{ color: "var(--sj-text-3)" }}>full access</div>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {catalog.map((grp) => (
              <React.Fragment key={grp.group}>
                <tr data-testid={`cat-group-${grp.group.replace(/\s+/g, "-").toLowerCase()}`}>
                  <td colSpan={roles.length + 1} className="py-2 px-3 text-[11px] font-bold uppercase tracking-wider sticky left-0" style={{ background: "var(--sj-bg)", color: "var(--sj-text-3)" }}>{grp.group}</td>
                </tr>
                {grp.items.map((it, idx) => {
                  const rowKey = `${grp.group}-${it.label}-${idx}`;
                  return (
                    <tr key={rowKey} className="border-t" style={{ borderColor: "var(--sj-border)" }} data-testid={`menu-row-${it.label.replace(/\s+/g, "-").toLowerCase()}`}>
                      <td className="py-2.5 px-3 font-medium sticky left-0 bg-white" style={{ color: "var(--sj-text)" }}>
                        {it.label}
                        {it.system && <span className="ml-2 text-[9px] uppercase font-bold px-1.5 py-0.5 rounded" style={{ background: "#E3F1EC", color: "#0B5E4E" }}>System</span>}
                      </td>
                      {roles.map((r) => {
                        if (it.system) {
                          return (
                            <td key={r.key} className="py-2.5 px-3 text-center">
                              <span className="inline-flex h-6 w-6 items-center justify-center rounded-md" title="Available to everyone — cannot be changed" style={{ background: "#C7CDD1", opacity: 0.7 }}>
                                <Check className="h-4 w-4 text-white" />
                              </span>
                            </td>
                          );
                        }
                        const on = !!matrix[r.key]?.[it.perm];
                        const locked = r.locked;
                        return (
                          <td key={r.key} className="py-2.5 px-3 text-center">
                            <button
                              type="button"
                              data-testid={`perm-${r.key}-${it.perm}`}
                              aria-checked={on}
                              role="checkbox"
                              disabled={locked}
                              onClick={() => toggle(r.key, it.perm)}
                              className={`inline-flex h-6 w-6 items-center justify-center rounded-md border transition-colors ${locked ? "cursor-not-allowed" : "cursor-pointer hover:border-[#0B5E4E]"}`}
                              style={{ borderColor: on ? "var(--sj-primary)" : "var(--sj-border)", background: on ? "var(--sj-primary)" : "#fff", opacity: locked ? 0.6 : 1 }}
                            >
                              {on && <Check className="h-4 w-4 text-white" />}
                            </button>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs" style={{ color: "var(--sj-text-3)" }}>
        Note: <b>Call members</b> also requires the member's team to have calling switched on (Teams page), which is off by default.
        Head of Department is the team leader; Assistant Head and Team Member sit below it.
      </p>

      <div className={`${CARD} p-4 sm:p-6`}>
        <button data-testid="toggle-change-log" onClick={() => setShowLog((s) => !s)} className="flex items-center gap-2 font-head text-lg font-semibold">
          <History className="h-5 w-5" style={{ color: "var(--sj-primary)" }} /> Permission change log
          <span className="text-xs font-normal ml-1" style={{ color: "var(--sj-text-3)" }}>({log.length})</span>
          <span className="text-xs underline ml-2" style={{ color: "var(--sj-primary)" }}>{showLog ? "hide" : "show"}</span>
        </button>
        {showLog && (
          <div className="mt-4 space-y-3" data-testid="change-log-list">
            {log.length === 0 ? (
              <p className="text-sm" style={{ color: "var(--sj-text-3)" }}>No permission changes recorded yet.</p>
            ) : log.map((e) => (
              <div key={e.id} data-testid={`log-entry-${e.id}`} className="rounded-xl p-3 border" style={{ borderColor: "var(--sj-border)", background: "var(--sj-bg)" }}>
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <span className="font-semibold">{e.by}</span>
                  <span style={{ color: "var(--sj-text-2)" }}>{e.action}</span>
                  <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded-full" style={{ background: e.scope === "global" ? "#FBEAD7" : "#E3F1EC", color: e.scope === "global" ? "#8a5a1a" : "var(--sj-primary)" }}>
                    {e.scope === "global" ? "Global" : (e.team_name || "Team")}
                  </span>
                  <span className="ml-auto text-xs" style={{ color: "var(--sj-text-3)" }}>{e.at ? ukDateTime(e.at) : ""}</span>
                </div>
                {(e.changes || []).length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {e.changes.map((c, i) => (
                      <span key={i} className="text-[11px] font-medium px-2 py-0.5 rounded-full" style={{ background: c.enabled ? "#E3F1EC" : "#FBE4E4", color: c.enabled ? "#0B5E4E" : "#B4433B" }}>
                        {c.role_label}: {c.enabled ? "+" : "−"}{c.permission_label}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
